import axios from "axios";
import moment from "moment";
import { v4 as uuidv4 } from "uuid";
import { CALENDAR_CONFIG } from "./CalendarComponents/calendarConstants.js";

// Status color mapping
export const STATUS_COLORS = {
    Pending: {
        backgroundColor: "#FEF3C7",
        color: "#B45309",
        borderColor: "#F59E0B",
        fullBorderColor: '#f59e0b33',
    },
    Approved: {
        backgroundColor: "#D5F3D8",
        color: "#70AD69",
        borderColor: "#73dc13",
        fullBorderColor: '#73dc1333',
    },
    Rejected: {
        backgroundColor: "#FFBBBD",
        color: "#A74E4E",
        borderColor: "#fb6464",
        fullBorderColor: '#fb646444',
    },
    Cancelled: {
        backgroundColor: "#FFBBBD",
        color: "#A74E4E",
        borderColor: "#fb6464",
        fullBorderColor: '#fb646444',
    },
    Birthday: {
        backgroundColor: "#f7cabc85",
        color: "#c86646",
        borderColor: "#ed9a81",
        fullBorderColor: '#ed9a8124',
    },
    Holiday: {
        backgroundColor: "#8B5CF61A",
        color: "#6D28D9",
        borderColor: "#8B5CF6",
        fullBorderColor: '#DED0FF',
    },
    Event: {
        backgroundColor: "#eaf6fd",
        color: "#0369A1",
        borderColor: "#0EA5E9",
        fullBorderColor: '#C4EDFF',
    },
    Anniversary: {
        backgroundColor: "#FFF7E6",
        color: "#D97706",
        borderColor: "#F59E0B",
        fullBorderColor: '#f59e0b3d',
    },
    GoogleEvent: {
        backgroundColor: "#E0F7FA",
        color: "#006064",
        borderColor: "#00ACC1",
        fullBorderColor: '#00acc129',
    },
    MicrosoftEvent: {
        backgroundColor: '#96A0DE',
        color: '#323c7a',
        borderColor: '#323c7a',
        fullBorderColor: '#323c7a33',
    },
};

// Category list for event filtering
export const CATEGORYLIST = [
    { name: 'Holidays', color: '#8B5CF6', eventTypes: ['Holiday'] },
    { name: 'Events', color: '#0EA5E9', eventTypes: ['Event', 'GoogleEvent', 'MicrosoftEvent'] },
    { name: 'Anniversaries', color: '#F59E0B', eventTypes: ['Anniversary'] },
    { name: 'Birthdays', color: '#FF8660', eventTypes: ['Birthday'] },
    { name: 'Time-Off', color: '#6A7988', eventTypes: ['Leave'] },
];

// Holidays for 2025
export const HOLIDAYS_2025 = [
    { date: "2025-01-01", name: "New Year's Day", description: "Global celebration of the new year" },
    { date: "2025-04-18", name: "Good Friday", description: "Christian holiday commemorating the crucifixion of Jesus" },
    { date: "2025-04-20", name: "Easter Sunday", description: "Christian holiday celebrating the resurrection of Jesus" },
    { date: "2025-05-01", name: "International Workers' Day", description: "Celebration of laborers and the working class" },
    { date: "2025-07-04", name: "Independence Day (US)", description: "US national holiday celebrating independence" },
    { date: "2025-10-02", name: "Gandhi Jayanti", description: "Indian holiday honoring Mahatma Gandhi's birthday" },
    { date: "2025-11-27", name: "Thanksgiving (US)", description: "US holiday for giving thanks and feasting" },
    { date: "2025-12-25", name: "Christmas Day", description: "Christian holiday celebrating the birth of Jesus" },
    { date: "2025-12-26", name: "Boxing Day", description: "Day for giving gifts to service workers" },
];

// Fetch leaves from the API
export const fetchLeaves = async (token) => {
    if (!token) throw new Error("No token found in Redux state");
    try {
        const response = await axios.get("/api/time-off-request/view-calendar", {
            headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
            },
        });
        // const filteredLeaves = response.data?.filter(
        //     (leave) => leave.status === "Pending" || leave.status === "Approved"
        // );
        return response.data;
    } catch (error) {
        throw new Error("Failed to fetch leave data");
    }
};

// Fetch holidays from the API
export const fetchHolidays = async (token) => {
    if (!token) throw new Error("No token found in Redux state");
    try {
        const response = await axios.post(
            "/api/holidays/data",
            {},
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
            }
        );
        return response.data;
    } catch (error) {
        throw new Error("Failed to fetch holiday data");
    }
};

// Fetch events from the API
export const fetchEvents = async (token) => {
    if (!token) throw new Error("No token found in Redux state");
    try {
        const response = await axios.post(
            "/api/events/data",
            {},
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
            }
        );
        return response.data;
    } catch (error) {
        throw new Error("Failed to fetch event data");
    }
};

// Fetch synced calendar import events (to disable already-synced events)
export const fetchSyncedImportEvents = async (token) => {
    if (!token) throw new Error("No token found in Redux state");
    try {
        const response = await axios.get("/api/time-track/importdata/synced-events", {
            headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
            },
        });
        return response.data;
    } catch (error) {
        throw new Error("Failed to fetch synced calendar import events");
    }
};

// Check Google Calendar integration status
export const checkGoogleCalendarIntegration = async (token, email) => {
    if (!token || !email) throw new Error('Token or email missing');
    try {
        const response = await axios.post(
            '/api/google-calendar/validate-email',
            { email },
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            }
        );
        return response.data;
    } catch (error) {
        console.error('Error checking Google Calendar integration:', error);
        return { exists: false, users: [] }; // Return default response on error
    }
};

// Check Google Calendar token validity
export const checkTokenValidity = async (token, user, setIsGoogleTokenChecking) => {
    if (!token || !user?._id || !user?.email) return { isValid: false, results: [] };
    setIsGoogleTokenChecking(true);
    try {
        const response = await axios.post(
            '/api/google-calendar/check-token',
            {
                userId: user._id,
                email: user.email,
            },
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            }
        );
        return response.data;
    } catch (error) {
        // Handle 404 errors gracefully - endpoint may not be available if Google Calendar is not enabled
        if (error.response?.status === 404) {
            // Silently return false for 404 errors (endpoint not found)
            return { isValid: false, results: [] };
        }
        // Only log non-404 errors
        if (error.response?.status !== 404) {
            console.error('Error checking token validity:', error);
        }
        return { isValid: false, results: [] };
    } finally {
        setIsGoogleTokenChecking(false);
    }
};

// Check Google Calendar enabled status
export const checkGoogleCalendarEnabledStatus = async (token, userId) => {
    if (!token || !userId) return false;
    try {
        const response = await axios.get(
            `/api/google-calendar/check-integration-status/${userId}`,
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
            }
        );
        return response.data.data.user_google_calendar_enabled;
    } catch (error) {
        console.error("Error checking Google Calendar enabled status:", error);
        return false;
    }
};

// Check Microsoft Calendar integration status
export const checkMicrosoftCalendarIntegration = async (token, email) => {
    if (!token || !email) return { exists: false, users: [] };
    try {
        const response = await axios.post(
            '/api/microsoft-calendar/validate-email',
            { email },
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            }
        );
        return response.data;
    } catch (error) {
        console.error('Error checking Microsoft Calendar integration:', error);
        return { exists: false, users: [] };
    }
};

// Check Microsoft Calendar token validity
export const checkMicrosoftTokenValidity = async (
    token,
    user,
    setIsMicrosoftTokenChecking
) => {
    if (!token || !user?._id || !user?.email) return { isValid: false, results: [] };
    setIsMicrosoftTokenChecking(true);
    try {
        const response = await axios.post(
            "/api/microsoft-calendar/check-token",
            {
                userId: user._id,
                email: user.email,
            },
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
            }
        );
        return response.data;
    } catch (error) {
        // Handle 401/404 errors gracefully - these are valid states (token invalid/expired or endpoint not available)
        if (error.response?.status === 401 || error.response?.status === 404) {
            // Silently return false for auth/not found errors (valid states)
            return { isValid: false, results: [] };
        }
        // Only log non-auth/not-found errors
        if (error.response?.status !== 401 && error.response?.status !== 404) {
            console.error("Error checking Microsoft token validity:", error);
        }
        return { isValid: false, results: [] };
    } finally {
        setIsMicrosoftTokenChecking(false);
    }
};

// Check Microsoft Calendar enabled status
export const checkMicrosoftCalendarEnabledStatus = async (token, userId) => {
    if (!token || !userId) return false;
    try {
        const response = await axios.get(
            `/api/microsoft-calendar/check-integration-status/${userId}`,
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
            }
        );
        return response.data.data.user_microsoft_calendar_enabled;
    } catch (error) {
        console.error("Error checking Microsoft Calendar enabled status:", error);
        return false;
    }
};

// Fetch Google Calendar events
export const fetchGoogleCalendarEvents = async (
    token,
    selectedUsers,
    setIsGoogleTokenInvalidModalOpen
) => {
    if (selectedUsers.length === 0) return [];
    const now = new Date();
    const timeMin = new Date(
        now.getFullYear() - 1,
        now.getMonth(),
        now.getDate()
    ).toISOString();
    const timeMax = new Date(
        now.getFullYear() + 1,
        now.getMonth(),
        now.getDate()
    ).toISOString();

    try {
        const response = await axios.post(
            "/api/get/google-calendar/events/multiple",
            {
                userIds: selectedUsers,
                timeMin,
                timeMax,
            },
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
            }
        );
        return response.data;
    } catch (error) {
        console.log("selectedGoogleEvents error:", error.message);
        setIsGoogleTokenInvalidModalOpen(true);
        throw error;
    }
};

// Fetch Microsoft Calendar events
export const fetchMicrosoftCalendarEvents = async (
    token,
    selectedUsers,
    setIsMicrosoftTokenInvalidModalOpen
) => {
    if (selectedUsers.length === 0) return [];
    const now = new Date();
    const timeMin = new Date(
        now.getFullYear() - 1,
        now.getMonth(),
        now.getDate()
    ).toISOString();
    const timeMax = new Date(
        now.getFullYear() + 1,
        now.getMonth(),
        now.getDate()
    ).toISOString();

    try {
        const response = await axios.post(
            "/api/get/microsoft-calendar/events/multiple",
            {
                userIds: selectedUsers,
                timeMin,
                timeMax,
            },
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
            }
        );
        return response.data;
    } catch (error) {
        console.log("selectedMicrosoftEvents error:", error.message);
        setIsMicrosoftTokenInvalidModalOpen(true);
        throw error;
    }
};

// Group events by key for calendar display
export const groupEventsByKey = (events) => {
    const groupedEvents = {};
    const displayTz = (global?.config?.getTimeZone && global.config.getTimeZone()) || 'UTC';

    events.forEach((event) => {
        // const userName = event.title.split(" - ")[0] || event.title.split("'s")[0] || event.title || "Holiday";
        const userName = event.titlePlain || htmlToPlainText(event.title) || event.title.split("'s")[0] || "Holiday";
        const eventType = event.eventType ||
            (event.title?.includes("Birthday") ? "Birthday" :
                event.title?.includes("Holiday") ? "Holiday" :
                    (event.title?.includes(" - ") ? event.title.split(" - ")[1] : "Leave"));
        const isHoliday =
            eventType === "Holiday" ||
            event?.extendedProps?.eventType === "Holiday";
        const uniqueId = event.id || uuidv4();
        const fmtKey = (d) =>
            isHoliday
                ? moment(d).format(CALENDAR_CONFIG.DATE_FORMAT) // ← no timezone for Holidays
                : moment.tz(d, displayTz).format(CALENDAR_CONFIG.DATE_FORMAT);
        const startKey = event.start
            ? fmtKey(event.start)
            : fmtKey(event.date);
        const endKey = event.end ? fmtKey(event.end) : startKey;

        const groupKey = event.start
            ? `${startKey}-${endKey}-${userName}-${eventType}-${event.startTime || 'no-start'}-${event.endTime || 'no-end'}-${uniqueId}`
            : `${startKey}-${userName}-${eventType}-${event.startTime || 'no-start'}-${event.endTime || 'no-end'}-${uniqueId}`;
        // const groupKey = event.start
        //     ? `${moment(event.start).format("YYYY-MM-DD")}-${moment(event.end).format("YYYY-MM-DD")}-${userName}-${eventType}-${event.startTime || 'no-start'}-${event.endTime || 'no-end'}-${uniqueId}`
        //     : `${event.date}-${userName}-${eventType}-${event.startTime || 'no-start'}-${event.endTime || 'no-end'}-${uniqueId}`;


        if (!groupedEvents[groupKey]) {
            groupedEvents[groupKey] = [];
        }
        groupedEvents[groupKey].push(event);
    });

    const grouped = Object.keys(groupedEvents).map((key) => {
        const eventsInGroup = groupedEvents[key];
        const firstEvent = eventsInGroup[0];

        let start = firstEvent.start || new Date(firstEvent.date);
        let end = firstEvent.end || new Date(firstEvent.date);
        // if (firstEvent.allDay) {
        //     start = moment.tz(start, global.config.getTimeZone() || 'UTC').startOf('day').toDate();
        //     end = moment.tz(end, global.config.getTimeZone() || 'UTC').startOf('day').toDate();
        // }
        const isHoliday =
            firstEvent.eventType === "Holiday" ||
            firstEvent?.extendedProps?.eventType === "Holiday";
        if (firstEvent.allDay) {
            if (isHoliday) {
                // ← NO timezone for Holidays
                const s = moment(start).startOf("day");
                const e = firstEvent.end
                    ? moment(end).startOf("day")
                    : s.clone().add(1, "day");
                start = s.toDate();
                end = e.toDate();
            } else {
                // other all-day types respect the display timezone
                const s = moment.tz(start, displayTz).startOf("day");
                const e = firstEvent.end
                    ? moment.tz(end, displayTz).startOf("day")
                    : s.clone().add(1, "day");
                start = s.toDate();
                end = e.toDate();
            }
        }

        return {
            id: key,
            title: eventsInGroup.length > 1 ? `${firstEvent.title} (+${eventsInGroup.length - 1})` : firstEvent.title,
            titlePlain: eventsInGroup.length > 1 ? `${firstEvent.titlePlain} (+${eventsInGroup.length - 1})` : firstEvent.titlePlain,
            start,
            end,
            backgroundColor: firstEvent.backgroundColor,
            textColor: firstEvent.color,
            borderColor: firstEvent.borderColor,
            fullBorderColor: firstEvent.fullBorderColor,
            allDay: firstEvent.allDay !== undefined ? firstEvent.allDay : !firstEvent.startTime,
            extendedProps: {
                allEvents: eventsInGroup,
                groupKey: key,
                profileImage: firstEvent.profileImage,
                description: firstEvent.description,
                status: firstEvent.status,
                user: firstEvent.user,
                typeOfLeave: firstEvent.typeOfLeave,
                issueDate: firstEvent.issueDate,
                totalDays: firstEvent.totalDays,
                startDate: firstEvent.startDate,
                endDate: firstEvent.endDate,
                startTime: firstEvent.startTime,
                endTime: firstEvent.endTime,
                note: firstEvent.note,
                leaveId: firstEvent.leaveId,
                eventType: firstEvent.eventType,
                eventName: firstEvent.eventName,
                eventLocation: firstEvent.eventLocation,
                eventOrganizer: firstEvent.eventOrganizer,
                eventOrganizerId: firstEvent.eventOrganizerId,
                eventAttendees: firstEvent.eventAttendees,
                eventBanner: firstEvent.eventBanner,
                eventIsVirtual: firstEvent.eventIsVirtual,
                eventMeetingLink: firstEvent.eventMeetingLink,
                repeatType: firstEvent.repeatType,
                source: firstEvent.source || firstEvent.extendedProps?.source,
                googleEventId: firstEvent.extendedProps?.googleEventId,
                googleCalendarEmail: firstEvent?.calendarEmail || null,
                microsoftEventId: firstEvent.extendedProps?.microsoftEventId,
                microsoftCalendarEmail: firstEvent?.calendarEmail || null,
                recurrence: firstEvent?.extendedProps?.recurrence || null,
                recurringEventId: firstEvent?.extendedProps?.recurringEventId || firstEvent?.extendedProps?.microsoftEventId || null,
                localEventId: firstEvent._id,
            },
        };
    });

    return grouped;
};

export function htmlToPlainText(html = '') {
    if (!html || typeof html !== 'string') return '';

    // Quick check for the Exchange boilerplate "empty" body
    const cleaned = html
        .replace(/<!--[^]*?-->/g, '') // strip comments
        .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '') // strip styles
        .replace(/<head[^>]*>[\s\S]*?<\/head>/gi, '') // strip head
        .replace(/<meta[^>]*>/gi, '')
        .replace(/<font[^>]*>|<\/font>/gi, '');

    // Normalize line breaks for common block elements
    let withBreaks = cleaned
        .replace(/<\s*br\s*\/?>/gi, '\n')
        .replace(/<\/\s*(p|div|li|h[1-6])\s*>/gi, '\n')
        .replace(/<\s*li\s*>/gi, '• ');

    // Create a temporary element to strip remaining tags and decode entities
    const tmp = document.createElement('div');
    tmp.innerHTML = withBreaks;
    let text = (tmp.textContent || tmp.innerText || '').replace(/\u00A0/g, ' ');

    // Collapse extra spaces/newlines
    text = text
        .replace(/\r/g, '')
        .replace(/[ \t]+\n/g, '\n')
        .replace(/\n{3,}/g, '\n\n')
        .trim();

    // If it's just empty/placeholder content, return empty
    if (!text || /^&nbsp;?$/.test(text) || text === 'PlainText' || text === 'Online') {
        return '';
    }
    return text;
}